import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, Check } from "lucide-react";
import moment from "moment";

import Button from "src/components/app/Button";
import Input from "src/components/app/Input";
import Dropdown from "src/components/common/Dropdown";
import Template from "src/components/app/smart-cale/Template";
import BluetoothCard from "src/components/app/permissions/BluetoothCard";
import ExitSmartScaleConnectionFlowBottomsheet from "src/components/app/bottom-sheet/devices/ExitSmartScaleConnectionFlowBottomsheet";

import {
  calculateAge,
  convertToFeetInches,
  formatFeetAndInchesToCm,
  parseMessageFromNative,
  sendMessageToParent,
} from "src/helpers/common";
import { POST_MESSAGE_EVENTS } from "src/constants/common";
import { DOB_AGE_LIMIT, genderOptions } from "src/constants/common";
import { useAppSelector } from "src/redux/hooks";
import BcaApis from "src/api/bca";
import Layout from "src/components/app/layout/Layout";
import supportPrograms from "src/data/app/programs";

function buildBcaPayloadFromEventData(
  eventData: Record<string, unknown>
): Record<string, unknown> {
  const d = (eventData?.data ?? eventData) as
    | Record<string, unknown>
    | undefined;
  if (!d || typeof d !== "object") return {};
  return {
    synced_device: "GoodFlip",
    device_id: "GoodFlip",
    weight: d.weight,
    bmi: d.BMI,
    bmr: d.BMR,
    fat_mass: d.fat_mass,
    muscle_mass: d.muscle_mass,
    bone_mass: d.bone_mass,
    hydration: d.hydration,
    protein: d.protein,
    subcutaneous_fat_percent: d.subcutaneous_fat_percent,
    visceral_fat: d.visceral_fat,
    skeletal_muscle_kg: d.skeletal_muscle_kg,
    skeletal_muscle_percent: d.skeletal_muscle_percent,
    subcutaneous_fat_kg: d.subcutaneous_fat_kg,
    fat_mass_kg: d.fat_mass_kg,
    hydration_kg: d.hydration_kg,
    protein_kg: d.protein_kg,
    metabolic_age: d.metabolic_age,
    body_fat_percentage: d.body_fat_percentage,
    obesity_rating: d.BMI,
    protein_rate: d.protein_rate,
    muscle_rate: d.muscle_rate,
    lean_body_mass: d.lean_body_mass,
    standard_weight: d.standard_weight,
    weight_control: d.weight_control,
    body_age: d.body_age,
  };
}

const SETUP_STEPS = [
  "Insert batteries into the weighing scale",
  "Place the scale on a flat surface",
  "Ensure your phone bluetooth is turned on",
  "Remove footwear and/or socks",
];

const START_PAIRING_STEPS = [
  "Tap on 'Start Pairing' to begin.",
  "Stand and wait on the scale for a few seconds.",
];

type ConnectionStep =
  | "intro"
  | "bluetooth"
  | "confirm-details"
  | "step-on-scale"
  | "loading"
  | "success"
  | "failure";

interface UserDetails {
  feet: string;
  inches: string;
  gender: string;
  dob: Date;
}

const SmartScale: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const navigate = useNavigate();
  const patient = useAppSelector((state) => state.auth.patient?.data);
  const [step, setStep] = useState<ConnectionStep>("intro");
  const defaultDob = moment().subtract(DOB_AGE_LIMIT.MAX, "years").toDate();
  const [userDetails, setUserDetails] = useState<UserDetails>({
    feet: "6",
    inches: "2",
    gender: "M",
    dob: defaultDob,
  });
  const [showError, setShowError] = useState(false);
  const [isHeightTouched, setIsHeightTouched] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(5);
  const [syncResult, setSyncResult] = useState<"pending" | "success" | "error">(
    "pending"
  );
  const [bluetoothStatus, setBluetoothStatus] = useState<
    "unknown" | "on" | "off" | "denied"
  >("unknown");
  const [deviceSyncData, setDeviceSyncData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const bcaApiCalledRef = useRef(false);

  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const bottomBarItems =
    config?.app?.screens && typeof config.app.screens === "object"
      ? Object.keys(config.app.screens).map((key) => {
          const screen = (config.app.screens as Record<string, any>)[key];
          return {
            screenName: screen?.title,
            icon: screen?.icon,
            activeIcon: screen?.activeIcon,
            screenKey: screen?.id,
          };
        })
      : [];

  const resetFlow = useCallback(() => {
    setStep("intro");
    setShowError(false);
    setIsHeightTouched(false);
    setBluetoothStatus("unknown");
    const p = patient as unknown as Record<string, unknown> | undefined;
    const heightCm =
      patient?.height != null ? Number(patient.height) : undefined;
    const { feet, inches } =
      heightCm != null
        ? convertToFeetInches(heightCm)
        : { feet: 6, inches: "2" };
    const dobFromPatient =
      patient?.dob &&
      moment(patient.dob as string, "YYYY-MM-DD", true).isValid()
        ? moment(patient.dob as string, "YYYY-MM-DD").toDate()
        : defaultDob;
    setUserDetails({
      feet: p?.height_ft?.toString() ?? feet.toString(),
      inches:
        p?.height_in?.toString() ??
        (typeof inches === "string" ? inches : String(inches)),
      gender: patient?.gender === "F" ? "F" : "M",
      dob: dobFromPatient,
    });
    setSuccessCountdown(5);
    setSyncResult("pending");
    setDeviceSyncData(null);
    bcaApiCalledRef.current = false;
  }, [patient]);

  useEffect(() => {
    if (patient) {
      const heightCm =
        patient?.height != null ? Number(patient.height) : undefined;
      if (heightCm != null) {
        const { feet, inches } = convertToFeetInches(heightCm);
        setUserDetails((prev) => ({
          ...prev,
          feet: prev.feet || feet.toString(),
          inches:
            prev.inches ||
            (typeof inches === "string" ? inches : String(inches)),
          gender: patient.gender === "F" ? "F" : prev.gender,
          dob:
            patient.dob && moment(patient.dob, "YYYY-MM-DD", true).isValid()
              ? moment(patient.dob, "YYYY-MM-DD").toDate()
              : prev.dob,
        }));
      }
    }
  }, [patient]);

  const handleClose = () => setShowExitWarning(true);
  const handleExitConfirm = () => {
    setShowExitWarning(false);
    navigate(-1);
  };
  const handleExitCancel = () => setShowExitWarning(false);

  const handleContinue = () => {
    if (step === "intro") setStep("bluetooth");
    else if (step === "bluetooth") setStep("confirm-details");
  };

  const handleRequestBluetoothPermission = () =>
    sendMessageToParent(POST_MESSAGE_EVENTS.REQUEST_BLUETOOTH_PERMISSION, {});
  const handleTurnOnBluetooth = () =>
    sendMessageToParent(POST_MESSAGE_EVENTS.TURN_ON_BLUETOOTH, {});
  const handleBluetoothContinue = () => {
    if (bluetoothStatus === "on") setStep("confirm-details");
  };

  const getBluetoothButtonLabel = () => {
    if (bluetoothStatus === "on") return "Continue";
    if (bluetoothStatus === "denied") return "Allow";
    return "Turn on Bluetooth";
  };
  const getBluetoothButtonHandler = () => {
    if (bluetoothStatus === "on") return handleBluetoothContinue;
    if (bluetoothStatus === "denied") return handleRequestBluetoothPermission;
    return handleTurnOnBluetooth;
  };

  const isValidDob = (date: Date) => {
    const str = moment(date).format("YYYY-MM-DD");
    return (
      moment(str, "YYYY-MM-DD", true).isValid() &&
      !moment(str).isAfter(moment())
    );
  };

  const handleConfirmDetails = () => {
    const feetNum = Number(userDetails.feet);
    const inchesNum = Number(userDetails.inches ?? "0");
    const dobValid = isValidDob(userDetails.dob);
    if (
      !dobValid ||
      feetNum > 8 ||
      feetNum <= 0 ||
      inchesNum < 0 ||
      inchesNum > 12 ||
      !userDetails.gender
    ) {
      setShowError(true);
      return;
    }
    setShowError(false);
    setStep("step-on-scale");
  };

  const handleStartPairing = () => {
    setStep("loading");
    setSyncResult("pending");
    setDeviceSyncData(null);
    bcaApiCalledRef.current = false;
    sendMessageToParent(POST_MESSAGE_EVENTS.START_BCA_PAIRING, {});
    const heightCm = formatFeetAndInchesToCm(
      userDetails.feet,
      userDetails.inches
    );
    const dobStr = moment(userDetails.dob).format("YYYY-MM-DD");
    const age = calculateAge(dobStr, "YYYY-MM-DD") ?? 25;
    const gender = userDetails.gender === "F" ? 2 : 1;
    const syncPayload = { age, height: heightCm, gender };
    setTimeout(
      () => sendMessageToParent(POST_MESSAGE_EVENTS.SYNC_DEVICE, syncPayload),
      1000
    );
    if (typeof window !== "undefined" && !(window as any).ReactNativeWebView) {
      setTimeout(
        () => setDeviceSyncData({ data: { weight: 70, BMI: 22 } }),
        2000
      );
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = parseMessageFromNative(event.data);
      if (!payload) return;
      const action = (payload.action || payload.type) as string;
      const data = payload.data as Record<string, unknown> | undefined;
      if (
        action === POST_MESSAGE_EVENTS.SYNC_SUCCESS ||
        action === POST_MESSAGE_EVENTS.DATA_RECEIVED
      ) {
        if (data && typeof data === "object") setDeviceSyncData(data);
      }
      if (
        action === POST_MESSAGE_EVENTS.SYNC_ERROR ||
        action === "TIMEOUT" ||
        action === "WRONG_SCALE_DETECTED"
      ) {
        setSyncResult("error");
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (
      step !== "loading" ||
      !deviceSyncData ||
      bcaApiCalledRef.current ||
      syncResult !== "pending"
    )
      return;
    bcaApiCalledRef.current = true;
    const payload = buildBcaPayloadFromEventData(deviceSyncData);
    BcaApis.setBCAReadings(payload)
      .then((res: any) => {
        const code = res?.code != null ? Number(res.code) : null;
        setSyncResult(code === -1 || code === 0 ? "error" : "success");
      })
      .catch(() => setSyncResult("error"));
  }, [step, deviceSyncData, syncResult]);

  useEffect(() => {
    if (step !== "loading" || syncResult !== "success") return;
    sendMessageToParent(POST_MESSAGE_EVENTS.BCA_SYNC_FINISHED, {
      status: "success",
    });
    setStep("success");
  }, [step, syncResult]);

  useEffect(() => {
    if (step !== "loading" || syncResult !== "error") return;
    sendMessageToParent(POST_MESSAGE_EVENTS.BCA_SYNC_FINISHED, {
      status: "error",
    });
    setStep("failure");
  }, [step, syncResult]);

  useEffect(() => {
    if (step !== "success") return;
    const t =
      successCountdown > 0
        ? setInterval(() => setSuccessCountdown((c) => c - 1), 1000)
        : null;
    return () => {
      if (t) clearInterval(t);
    };
  }, [step, successCountdown]);

  useEffect(() => {
    if (step === "success" && successCountdown <= 0) {
      navigate(
        `/app/${supportProgramKey}/insights-screen?tab=vitals&sub-tab=body-composition`
      );
    }
  }, [step, successCountdown, supportProgramKey, navigate]);

  const handleDone = () =>
    navigate(
      `/app/${supportProgramKey}/insights-screen?tab=vitals&sub-tab=body-composition`
    );

  const renderFooter = () => {
    if (step === "loading") return null;
    if (step === "intro")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleContinue}
            variant="contained"
            rounded="full"
            fullWidth
          >
            Continue
          </Button>
        </div>
      );
    if (step === "bluetooth")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={getBluetoothButtonHandler()}
            variant="contained"
            rounded="full"
            fullWidth
          >
            {getBluetoothButtonLabel()}
          </Button>
        </div>
      );
    if (step === "confirm-details")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleConfirmDetails}
            rounded="full"
            fullWidth
            variant="contained"
          >
            Submit
          </Button>
        </div>
      );
    if (step === "step-on-scale")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleStartPairing}
            rounded="full"
            fullWidth
            variant="contained"
          >
            Start Pairing
          </Button>
        </div>
      );
    if (step === "success")
      return (
        <div className="p-4 bg-white border-t border-gray-100">
          <Button
            onClick={handleDone}
            rounded="full"
            fullWidth
            variant="contained"
          >
            Done
          </Button>
        </div>
      );
    if (step === "failure")
      return (
        <div className="space-y-3 p-4 bg-white border-t border-gray-100">
          <Button
            onClick={resetFlow}
            rounded="full"
            fullWidth
            variant="contained"
          >
            Start Over
          </Button>
          <Button
            // onClick={() => navigate(-1)}
            onClick={handleClose}
            rounded="full"
            fullWidth
            variant="outlined"
          >
            Cancel
          </Button>
        </div>
      );
    return null;
  };

  return (
    <>
      <Layout supportProgramKey={supportProgramKey} footer={renderFooter()}>
        <div className="relative px-6 pb-8 max-w-lg h-full flex-1 mx-auto flex flex-col items-center justify-center">
          <div className="absolute top-4 left-4">
            <button
              type="button"
              onClick={handleClose}
              className="w-9 h-9 rounded-full bg-white/90 border border-gray-200 shadow-sm flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          {step === "intro" && (
            <Template
              title="Let's Connect your Smart Scale"
              titleClassName="text-[1.75rem] font-semibold text-center mt-4 text-gray-900"
              description={
                <ul className="space-y-3">
                  {SETUP_STEPS.map((text, i) => (
                    <li key={i} className="flex items-start gap-3 text-left">
                      <span className="w-6 h-6 rounded-full bg-[var(--app-primary-background)] text-[var(--app-primary)] text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-[0.98rem] leading-6 text-gray-700">
                        {text}
                      </p>
                    </li>
                  ))}
                </ul>
              }
            />
          )}

          {step === "bluetooth" && (
            <BluetoothCard onStatusChange={setBluetoothStatus} />
          )}

          {step === "confirm-details" && (
            <>
              <h1 className="pt-4 pb-3 text-2xl font-bold text-[var(--text-primary)]">
                Enter your details
              </h1>
              <p className="text-sm text-[var(--text-secondary)] opacity-80 mb-6">
                Enter the following details to continue
              </p>
              <div className="flex flex-col gap-4 mb-8">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <p className="text-[var(--text-primary)] font-medium mb-3">
                    Enter your height <span className="text-red-500">*</span>
                  </p>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Input
                        name="feet"
                        label="Feet"
                        type="number"
                        inputMode="numeric"
                        placeholder="Feet"
                        value={userDetails.feet}
                        onChange={(e) => {
                          if (/^\d*$/.test(e.target.value)) {
                            setIsHeightTouched(true);
                            setShowError(false);
                            setUserDetails((u) => ({
                              ...u,
                              feet: e.target.value,
                            }));
                          }
                        }}
                        error={showError || isHeightTouched}
                        helperText={
                          showError || isHeightTouched
                            ? "Feet should be between 1 and 8."
                            : undefined
                        }
                        fullWidth
                      />
                      {(showError || isHeightTouched) &&
                        (Number(userDetails.feet) > 8 ||
                          Number(userDetails.feet ?? "0") <= 0) && (
                          <p className="text-xs text-red-500 mt-1">
                            Feet should be between 1 and 8.
                          </p>
                        )}
                    </div>
                    <div className="flex-1">
                      <Input
                        name="inches"
                        label="Inches"
                        type="number"
                        inputMode="numeric"
                        placeholder="Inches"
                        value={userDetails.inches}
                        onChange={(e) => {
                          if (/^\d*\.?\d*$/.test(e.target.value)) {
                            setShowError(false);
                            setUserDetails((u) => ({
                              ...u,
                              inches: e.target.value,
                            }));
                          }
                        }}
                        error={showError || isHeightTouched}
                        helperText={
                          showError || isHeightTouched
                            ? "Inches should be between 0 and 12."
                            : undefined
                        }
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <p className="text-[var(--text-primary)] font-medium mb-3">
                    What is your gender? <span className="text-red-500">*</span>
                  </p>
                  <div className="flex flex-col gap-2">
                    {genderOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          setUserDetails((u) => ({ ...u, gender: opt.value }))
                        }
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 text-left"
                      >
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            userDetails.gender === opt.value
                              ? "border-[var(--app-primary)] bg-white"
                              : "border-gray-400"
                          }`}
                        >
                          {userDetails.gender === opt.value && (
                            <div className="w-2.5 h-2.5 rounded-full bg-[var(--app-primary)]" />
                          )}
                        </div>
                        <span className="text-[var(--text-primary)]">
                          {opt.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  {showError && !userDetails.gender && (
                    <p className="text-xs text-red-500 mt-1">
                      Please select gender.
                    </p>
                  )}
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <p className="text-[var(--text-primary)] font-medium mb-3">
                    Enter your date of birth{" "}
                    <span className="text-red-500">*</span>
                  </p>
                  <div className="flex gap-3">
                    <Dropdown
                      name="day"
                      options={Array.from({ length: 31 }, (_, i) => ({
                        value: i + 1,
                        label: i + 1,
                      }))}
                      value={userDetails.dob.getDate()}
                      onChange={(value) => {
                        const d = new Date(userDetails.dob);
                        d.setDate(Number(value));
                        setUserDetails((u) => ({ ...u, dob: d }));
                        setShowError(false);
                      }}
                      placeholder="Day"
                      width={80}
                    />
                    <Dropdown
                      name="month"
                      options={[
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ].map((label, index) => ({ value: index + 1, label }))}
                      value={userDetails.dob.getMonth() + 1}
                      onChange={(value) => {
                        const d = new Date(userDetails.dob);
                        d.setMonth(Number(value) - 1);
                        setUserDetails((u) => ({ ...u, dob: d }));
                        setShowError(false);
                      }}
                      placeholder="Month"
                      fullWidth
                    />
                    <Dropdown
                      name="year"
                      options={Array.from(
                        { length: DOB_AGE_LIMIT.MIN - DOB_AGE_LIMIT.MAX + 1 },
                        (_, i) => {
                          const y = moment().year() - DOB_AGE_LIMIT.MAX - i;
                          return { value: y, label: y };
                        }
                      )}
                      value={userDetails.dob.getFullYear()}
                      onChange={(value) => {
                        const d = new Date(userDetails.dob);
                        d.setFullYear(Number(value));
                        setUserDetails((u) => ({ ...u, dob: d }));
                        setShowError(false);
                      }}
                      placeholder="Year"
                      width={100}
                    />
                  </div>
                  {showError && !isValidDob(userDetails.dob) && (
                    <p className="text-xs text-red-500 mt-1">
                      Please enter a valid date.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {step === "step-on-scale" && (
            <Template
              title="Connecting to your Smart Scale..."
              titleClassName="text-[1.75rem] font-semibold text-center mt-4 text-gray-900"
              description={
                <ul className="space-y-3">
                  {START_PAIRING_STEPS.map((text, i) => (
                    <li key={i} className="flex items-start gap-3 text-left">
                      <span className="w-6 h-6 rounded-full bg-[var(--app-primary-background)] text-[var(--app-primary)] text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-[0.98rem] leading-6 text-gray-700">
                        {text}
                      </p>
                    </li>
                  ))}
                </ul>
              }
            />
          )}

          {step === "loading" && (
            <Template
              title={
                deviceSyncData
                  ? "Saving your readings..."
                  : "Listening to device data"
              }
              titleClassName="text-lg font-semibold text-center mt-4 text-gray-900"
              description={
                <p className="text-sm text-[var(--text-secondary)]">
                  {deviceSyncData
                    ? "Please wait."
                    : "Please stay on the scale..."}
                </p>
              }
            />
          )}

          {step === "success" && (
            <Template
              title="Success"
              titleClassName="text-green-500"
              description={
                <p className="text-center text-[var(--text-secondary)]">
                  We have successfully captured your health metrics. You will be
                  redirected to the <b>Insights</b> in {successCountdown}{" "}
                  seconds.
                </p>
              }
              image={
                <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center">
                  <Check className="w-12 h-12 text-white" />
                </div>
              }
            />
          )}

          {step === "failure" && (
            <Template
              title="Connection Failed"
              titleClassName="text-xl font-bold text-center mt-6 text-red-500"
              description={
                <p className="text-center text-[var(--text-secondary)] mt-2 mb-8">
                  Please try again. Ensure the scale is on and you are standing
                  on it. If the issue persists, please contact support.
                </p>
              }
            />
          )}
        </div>
      </Layout>

      {showExitWarning && (
        <ExitSmartScaleConnectionFlowBottomsheet
          show
          onExit={handleExitConfirm}
          onCancel={handleExitCancel}
        />
      )}
    </>
  );
};

export default SmartScale;
